import { z } from 'zod';

/** Canaux de distribution OTA de l'app mobile. */
export const OTA_CHANNELS = ['prod', 'preprod'] as const;
export type OtaChannelName = (typeof OTA_CHANNELS)[number];

/** Version d'un bundle OTA, telle qu'elle figure dans le nom du zip (bundle-<version>.zip). */
export const OtaBundleVersionSchema = z.string().regex(/^[\w.+-]{1,64}$/, 'Version de bundle invalide');

/**
 * Pilotage d'un canal (espace /superadmin), appliqué par l'API au manifest servi aux téléphones :
 * - `pinned_version` : retour arrière vers un bundle encore présent sur le serveur (null = dernier bundle publié) ;
 * - `min_native_build` : versionCode natif minimal ; en dessous, l'app demande une mise à jour depuis le store.
 */
export const MobileReleaseControlSchema = z.object({
  pinned_version: OtaBundleVersionSchema.nullable(),
  min_native_build: z.number().int().min(0).max(100_000).nullable(),
  native_update_message: z.string().trim().max(300),
  android_store_url: z.string().trim().url('URL invalide').max(500).nullable(),
  ios_store_url: z.string().trim().url('URL invalide').max(500).nullable(),
});
export type MobileReleaseControl = z.infer<typeof MobileReleaseControlSchema>;

export const DEFAULT_MOBILE_RELEASE_CONTROL: MobileReleaseControl = {
  pinned_version: null,
  min_native_build: null,
  native_update_message: '',
  android_store_url: null,
  ios_store_url: null,
};

export const MobileReleasesControlSchema = z.partialRecord(z.enum(OTA_CHANNELS), MobileReleaseControlSchema);
export type MobileReleasesControl = z.infer<typeof MobileReleasesControlSchema>;

/** Manifest OTA tel que publié par la CI, plus les champs ajoutés par l'API selon le pilotage du canal. */
export interface OtaManifestPayload {
  version: string;
  url: string;
  checksum: string;
  minBuildNumber: number;
  mandatory: boolean;
  releaseNotes?: string;
  publishedAt: string;
  signature?: string;
  /** Retour arrière demandé : à installer même si la version est inférieure à celle du téléphone. */
  rollback?: boolean;
  /** versionCode natif minimal : en dessous, l'app bloque et renvoie vers le store. */
  minNativeBuild?: number;
  nativeUpdateMessage?: string;
  storeUrls?: { android?: string; ios?: string };
}

export interface OtaBundleInfo {
  filename: string;
  version: string;
  size: number;
  uploadedAt: string;
}

export interface MobileReleaseChannelReport {
  channel: OtaChannelName;
  /** Manifest publié par la CI (null si absent ou illisible). */
  published: OtaManifestPayload | null;
  publishedError: string | null;
  /** Version réellement distribuée aux téléphones (retour arrière compris). */
  effectiveVersion: string | null;
  bundles: OtaBundleInfo[];
  control: MobileReleaseControl;
}
